<template>
  <v-col cols="12" md="6" lg="4" xl="3">
    <v-card outlined tile class="campaign-card" height="444">
      <div style="height: 180px" class="pointer" @click="showGiftOptions()">
        <campaign-card-images
          :products="availableItemsInGift"
          :disabled="isCampaignDisabled"
        />
      </div>

      <v-card-text class="d-flex px-6 py-4 justify-space-between">
        <div class="campaign-card__labels">
          <update-status-sheet
            v-if="campaignCreator"
            color="#D2F5F3"
            text-color="#004642"
            v-tooltip="{
              content: `Created by: ${campaignCreator}`,
              ...standardTooltipProps,
            }"
          >
            BY: {{ campaignCreator.slice(0, 40) }}
          </update-status-sheet>

          <update-status-sheet
            v-if="isPreProduction"
            color="#ECD884"
            text-color="#222325"
            v-tooltip="{
              content: `Items will be ordered and produced once all eGift recipients have made their selections,
                or when the campaign ends. Track redemptions and details through the &quot;My eGift orders&quot;
                on your dashboard`,
              ...standardTooltipProps,
            }"
          >
            Zero waste
          </update-status-sheet>

          <v-popover
            placement="top"
            trigger="hover"
            :container="false"
          >
            <update-status-sheet
              v-if="isAutomatedFail"
              color="#FFEBEC"
              text-color="#9D1216"
            >
              Automation Fail
            </update-status-sheet>
            <template slot="popover">
              <div class="campaign-card__automation-fail-tooltip">
                <div>
                  <strong>{{ automationFailsCount }}</strong> automated gift sends failed. Click the button to review and resend them.
                </div>
                <CgButton outlined :height="32" @click="handleRedirectToReviewFailedSends">
                  Review Failed Sends
                </CgButton>
              </div>
            </template>
          </v-popover>

          <update-status-sheet
            v-if="isAutomated && !isAutomatedFail"
            color="#E5DFFF"
            text-color="#4E3AAF"
            v-tooltip="{
              content: automationTooltip,
              ...standardTooltipProps,
            }"
          >
            Automated
          </update-status-sheet>
        </div>

        <the-campaign-card-settings
          :gift-options="giftOptions"
          :is-automated="isAutomated"
          :user-permissions="userPermissions"
          :campaign-permissions="campaignPermissions"
          @onGiftOptions="showGiftOptions()"
          @onCampaignSettings="showCampaignSettingsPanel()"
          @onEstimateCost="showEstCostPanel()"
          @onAddAutomation="showAutomationPanel()"
          @onPreview="previewGift()"
          @onEdit="editCampaign()"
          @onDuplicate="duplicateCampaign()"
          @on-view-analytics="viewAnalytics()"
        />
      </v-card-text>

      <v-card-text class="py-0 px-6">
        <h1 class="campaign-card__name">
          {{ item.name }}
        </h1>

        <v-row dense class="pt-2">
          <campaign-card-type
            :gift-type="campaignType"
            :price-range="item.prices_range"
            :gift-settings="item.egift_settings"
            :gift-products="item.collection?.items || []"
            :is-prepaid="!!Number(item.is_prepaid)"
          />

          <campaign-card-tags :tags="item.tags.map((tag) => tag.name)" />
        </v-row>
      </v-card-text>

      <v-card-actions class="px-6 pb-7">
        <campaign-card-button
          @showSendEGiftPanel="showSendEGift"
          @showSendDirectPanel="$emit('showSendDirectPanel')"
          :gift-type="campaignType"
          :items="item.collection?.items || []"
          :disabled="
            !(item.collection?.items || [])
              .map(item => item.product.qty.is_out_of_stock)
              .includes(false)
            || isCampaignDisabled
          "
        />
      </v-card-actions>
    </v-card>
  </v-col>
</template>

<script>
import userIsCgElite from '@/mixins/userIsCgElite'
import standardTooltipProps from '@/mixins/standardTooltipProps'
import { sanitizeUrl } from '@/utils/security';

import CampaignCardImages from './TheCampaignCardImages.vue'
import CampaignCardButton from './CampaignCardSendButton.vue'
import CampaignCardType from './TheCampaignCardType.vue'
import TheCampaignCardSettings from './TheCampaignCardSettings.vue'
import CampaignCardTags from './TheCampaignCardTags.vue'
import UpdateStatusSheet from '@/components/UpdateStatusSheet'
import { CgButton } from '@corporategiftcom/design-system'

export default {
  name: 'CampaignCard',
  mixins: [
    userIsCgElite,
    standardTooltipProps,
  ],
  components: {
    TheCampaignCardSettings,
    CampaignCardTags,
    CampaignCardImages,
    CampaignCardButton,
    UpdateStatusSheet,
    CampaignCardType,
    CgButton
  },
  props: {
    item: {
      type: Object,
      required: true
    },
    userPermissions: {
      type: Array,
      required: false,
      default: () => ([]),
    },
  },
  computed: {
    campaignType () {
      return this.item.egift_settings.egift_type || 'single'
    },
    isPreProduction () {
      return this.item?.is_pre_production ?? false
    },
    isAutomated () {
      return this.item?.has_automation ?? false
    },
    isAutomatedFail () {
      return this.item?.failed_automation_actions.length > 0 ?? false
    },
    campaignPermissions () {
      return this.item?.permissions ?? []
    },
    giftOptions () {
      const { item } = this
      const settings = item?.egift_settings || {}
      const items = item?.collection?.items || []
      if (settings?.egift_type === 'coin') {
        return items?.filter(item => item.product.price <= Number(settings?.coin_value || 0))?.length || 0
      }
      return items.length
    },
    availableItemsInGift () {
      const { egift_settings: settings, collection } = this.item
      const items = collection?.items ?? []
      let products = items

      if (settings?.egift_type === 'coin') {
        products =  items?.filter(item => item.product.price <= Number(settings?.coin_value || 0))
      }

      return products?.sort((itemA, itemB) => {
        const a = itemA?.product?.qty?.is_out_of_stock ?? false
        const b = itemB?.product?.qty?.is_out_of_stock ?? false

        return a - b
      }) ?? []
    },
    isCampaignDisabled () {
      return this.item?.is_disabled
    },
    campaignCreator () {
      return this.item?.created_by || ''
    },
    automationTooltip () {
      const { integration, source, type } = this.item?.automation ?? {}

      if (source === 'contacts') {
        return `This campaign is sent automatically to My Contacts group/s on their ${type}.
          <br>
          <br>
          Click <i>Edit automation</i> to see details
        `
      } else {
        return `This campaign is sent automatically to ${integration?.name} group/s on their ${type}.
          <br>
          <br>
          Click <i>Edit automation</i> to see details
        `
      }
    },
    automationFailsCount () {
      return this.item?.failed_automation_actions.length || 0;
    },
  },
  methods: {
    showGiftOptions () {
      this.$emit('showGiftOptionsPanel', this.item)
    },
    showEstCostPanel () {
      this.$emit('showEstCostPanel', this.item)
    },
    showAutomationPanel () {
      this.$emit('showAutomationPanel', this.item)
    },
    showCampaignSettingsPanel () {
      this.$emit('showCampaignSettingsPanel', this.item)
    },
    showSendEGift (type) {
      this.$emit('showEGiftPanel', this.item, type)
    },
    previewGift () {
      const {
        video_greeting: rawVideo,
        calendly_link: rawLink,
        greeting_message: message,
        gift_giver_name: giverName,
        logo_url: campaignLogo,
        customer_group: { logo },
        egift_settings: {
          is_regift_enabled: canRegift,
          is_upgrade_enabled: canUpgrade
        },
        entity_id: campaignId,
        use_free_form_url: useFreeFormUrl = false,
        use_calendly_link: useCalendlyLink = false,
        free_form_url: rawFreeFormUrl = '',
        free_form_button: freeFormButton = '',
      } = this.item

      const logoUrl = campaignLogo || `https://corporategift.com/media/companies_logo/${logo}`

      const freeFormUrl = sanitizeUrl(rawFreeFormUrl)

      const link = sanitizeUrl(rawLink)

      const video = sanitizeUrl(rawVideo)

      const queryObject = {
        logo_url: logoUrl,
        can_regift: +canRegift,
        can_upgrade: +canUpgrade,
        sender: giverName
      }

      if (video) queryObject.video_greeting = video

      if (useCalendlyLink && link) {
        queryObject.calendly_link = link
      }
      else if (freeFormUrl && freeFormButton) {
        queryObject.free_form_url = freeFormUrl
        queryObject.free_form_button = freeFormButton
      }

      if (message) queryObject.greeting_message = message.replaceAll('\n', '<br />')

      const queryParams = new URLSearchParams(queryObject)
      const egiftLink = process.env.VUE_APP_EGIFT_URL || 'https://egift.staging.corporategift.com'
      window.open(`${egiftLink}/recipient/preview/c/${btoa(campaignId)}/gift?${queryParams}`, '_blank')
    },
    editCampaign () {
      const { entity_id: campaignId } = this.item

      if (campaignId) {
        this.$router.push({ name: 'CreateCampaignReview', params: { campaignId } })
      }
    },
    duplicateCampaign () {
      const { entity_id: campaignId } = this.item

      if (campaignId) {
        this.$router.push({
          name: 'CreateCampaignReview',
          params: { campaignId: 'create' },
          query: { duplicateFrom: campaignId },
        })
      }
    },
    viewAnalytics () {
      const { entity_id: campaignId } = this.item

      if (campaignId) {
        // Properly encode the campaign ID to prevent security vulnerabilities
        const encodedCampaignId = encodeURIComponent(campaignId);
        window.location.href = `${window.location.origin}/e-gift-orders/campaign/${encodedCampaignId}`;
      }
    },
    handleRedirectToReviewFailedSends() {
      const { entity_id: campaignId } = this.item

      if (campaignId) {
        // Properly encode the campaign ID to prevent security vulnerabilities
        const encodedCampaignId = encodeURIComponent(campaignId);
        window.location.href = `${window.location.origin}/e-gift-orders/campaign/${encodedCampaignId}?showReviewFailedSends=true`;
      }
    }
  }
}
</script>

<style lang="scss" scoped>
.campaign-card {
  background: #fafafa;
  box-shadow: 0px 0px 4px rgba(0, 0, 0, 0.08);
  border-color: #F4F4F4 !important;

  &__name {
    font-family: 'Lato-Regular', sans-serif;
    line-height: 32px;
    font-weight: normal;
    font-size: 26px;
    color: #000;
    cursor: default;
    display: -webkit-box;
    -webkit-line-clamp: 2;
    -webkit-box-orient: vertical;
    overflow: hidden;
    text-overflow: ellipsis;
  }

  &__labels {
    display: flex;
    flex-wrap: wrap;
    flex-direction: row;
    gap: 8px;
  }

  &__automation-fail-tooltip {
    position: relative;
    display: flex;
    flex-direction: column;
    gap: 8px;
    padding: 22px 12px 80px 19px;
    width: 270px;
    height: 140px;
    background: #FFEBEC;
    border: 1px solid #FFBDBF;
    border-radius: 4px;
    pointer-events: auto;
    margin-bottom: 10px;
    color: #222325;
    font-size: 14px;
    font-style: normal;
    font-weight: 400;
    line-height: 136%;

    ::v-deep .cg-button {
      border-radius: 80px;
      width: fit-content;
      color: #222325;
      text-align: center;
      font-size: 14px;
      font-style: normal;
      font-weight: 600;
      line-height: normal;
      padding: 8px 24px;
    }

    & > div {
      color: #222325;
    }

    &::after {
      content: '';
      position: absolute;
      bottom: -8px;
      left: 50%;
      transform: translateX(-50%);
      width: 0;
      height: 0;
      border-left: 8px solid transparent;
      border-right: 8px solid transparent;
      border-top: 8px solid #FFEBEC;
    }

    &::before {
      content: '';
      position: absolute;
      bottom: -9px;
      left: 50%;
      transform: translateX(-50%);
      width: 0;
      height: 0;
      border-left: 9px solid transparent;
      border-right: 9px solid transparent;
      border-top: 9px solid #FFBDBF;
    }
  }
}

.v-card__actions {
  bottom: 0;
  position: absolute;
  width: 100%;
}
</style>
